import type {HookActivator, HookActivatorHolder} from "OK/HookActivator";
import {isElementPartiallyInViewport} from "OK/viewport";

export let io: IntersectionObserver | null = null;

export interface IViewportActivatorsHolder extends Element {
    $viewportActivators: Set<HookActivator>
}

const viewportMargin = {
    top: -100,
    bottom: -100
};

function getObserver() {
    if (io === null && (typeof IntersectionObserver !== 'undefined')) {
        io = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if ((entry.target as IViewportActivatorsHolder).$viewportActivators) {
                        (entry.target as IViewportActivatorsHolder).$viewportActivators.forEach(a => a.onView());
                    }
                }
            })
        }, {
            rootMargin: `${viewportMargin.top}px 0px ${viewportMargin.bottom}px 0px`,
            threshold: 0
        });
    }
    return io;
}

function getIOTarget(el: Element) {
    if (el === document.body) {
        return null;
    }
    if (el.clientWidth && el.clientHeight) {
        return el as IViewportActivatorsHolder;
    } else {
        return getIOTarget(el.parentElement!);
    }
}

export const viewportTargets: HookActivator[] = [];

export function isVisible(el: Element) {
    return isElementPartiallyInViewport(el as HTMLElement, 0.01, viewportMargin)
}

function logError(msg: string, e: Error) {
    console.error('[HookViewportActivator]', msg,  e);
    import('OK/logger').then(logger => logger.errorEx(e, msg, ''));
}

export function isActivatorVisible(hookActivator: HookActivator) {
    try {
        return isVisible(hookActivator.el) || isVisible(hookActivator.ioTarget!);
    } catch (e) {
        logError('Error while checking element visibility, hook=' + hookActivator.moduleName, e as Error);
        return true;
    }
}

export function checkArea(el: HookActivatorHolder) {
    if (!viewportTargets.length) {
        return;
    }
    if (el.$activator && isActivatorVisible(el.$activator)) {
        el.$activator.onView();
    }
    const ln = el.children.length;
    for (let i = 0; i < ln; i++) {
        checkArea(el.children[i] as HookActivatorHolder);
    }
}

export function checkAll() {
    for (let i = viewportTargets.length - 1; i >= 0; i--) {
        const ha = viewportTargets[i];
        if (isActivatorVisible(ha)) {
            ha.onView();
        }
    }
}

export function observe(hookActivator: HookActivator): void {
    const io = getObserver()
    if (!io) {
        hookActivator.onView();
        return;
    }

    const ioTarget = getIOTarget(hookActivator.el);

    if (!ioTarget) {
        hookActivator.onView();
        return;
    }

    if (isVisible(ioTarget)) {
        hookActivator.onView();
        return;
    }

    hookActivator.ioTarget = ioTarget;
    io.observe(ioTarget);
    viewportTargets.push(hookActivator);
    if (!ioTarget.$viewportActivators) {
        ioTarget.$viewportActivators = new Set<HookActivator>();
    }
    ioTarget.$viewportActivators.add(hookActivator);
}

export function unobserve(hookActivator: HookActivator) {
    const ioTarget = hookActivator.ioTarget;
    if (!ioTarget) {
        return;
    }
    if (ioTarget.$viewportActivators) {
        ioTarget.$viewportActivators.delete(hookActivator);
    }
    if (!ioTarget.$viewportActivators || ioTarget.$viewportActivators.size === 0) {
        io?.unobserve(ioTarget);
    }
    const idx = viewportTargets.indexOf(hookActivator);
    if (idx !== -1) {
        viewportTargets.splice(idx, 1);
    }
}
